import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchFaqs, createFaq, updateFaq, deleteFaq } from '../services/faqs';
import './FAQs.css';

const FAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    display_order: 0,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadFaqs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchFaqs();
      setFaqs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load FAQs.');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'display_order' ? (value === '' ? 0 : parseInt(value, 10) || 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!formData.question.trim() || !formData.answer.trim()) {
      setError('Question and answer are required.');
      return;
    }
    try {
      if (editingFaq) {
        await updateFaq(editingFaq.id, {
          question: formData.question.trim(),
          answer: formData.answer.trim(),
          display_order: formData.display_order,
        });
        setSuccess('FAQ updated successfully.');
      } else {
        await createFaq({
          question: formData.question.trim(),
          answer: formData.answer.trim(),
          display_order: formData.display_order,
        });
        setSuccess('FAQ created successfully.');
      }
      setShowModal(false);
      resetForm();
      loadFaqs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save FAQ.');
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', display_order: 0 });
    setEditingFaq(null);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      display_order: faq.display_order ?? 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await deleteFaq(id);
      setSuccess('FAQ deleted successfully.');
      loadFaqs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete FAQ.');
    }
  };

  return (
    <div className="admin-faqs-page">
      <AdminPageTitle title="FAQs" />
      <div className="page-header">
        <h1>FAQ Management</h1>
        <button type="button" className="btn-primary" onClick={handleAddNew}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add FAQ
        </button>
      </div>

      {error && (
        <div className="faqs-alert faqs-alert-error">
          {error}
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="faqs-alert faqs-alert-success">
          {success}
          <button type="button" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="faqs-loading">Loading FAQs...</div>
      ) : faqs.length === 0 ? (
        <div className="faqs-empty">
          <p>No FAQs yet. Click &quot;Add FAQ&quot; to create questions and answers for the customer FAQs page.</p>
        </div>
      ) : (
        <div className="faqs-table-wrapper">
          <table className="faqs-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Question</th>
                <th>Answer</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((faq) => (
                <tr key={faq.id}>
                  <td className="faqs-order">{faq.display_order}</td>
                  <td className="faqs-question-cell">{faq.question}</td>
                  <td className="faqs-answer-cell">{faq.answer.length > 80 ? `${faq.answer.slice(0, 80)}…` : faq.answer}</td>
                  <td className="faqs-actions">
                    <button type="button" className="btn-edit" onClick={() => handleEdit(faq)}>Edit</button>
                    <button type="button" className="btn-delete" onClick={() => handleDelete(faq.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</h2>
              <button type="button" className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="faqs-form">
              <div className="form-group">
                <label htmlFor="faq-question">Question *</label>
                <input
                  type="text"
                  id="faq-question"
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  placeholder="Enter the question"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="faq-answer">Answer *</label>
                <textarea
                  id="faq-answer"
                  name="answer"
                  value={formData.answer}
                  onChange={handleInputChange}
                  placeholder="Enter the answer"
                  rows={5}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="faq-order">Display order</label>
                <input
                  type="number"
                  id="faq-order"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  min={0}
                />
                <small>Lower numbers appear first on the FAQs page.</small>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn-primary">{editingFaq ? 'Update' : 'Create'} FAQ</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;
